/* ==========================================================================
   HyCraft x HydroShield - Mega menu
   Controla la apertura del panel de navegacion de ancho completo.
   ========================================================================== */

(function () {
  'use strict';

  if (typeof customElements === 'undefined' || customElements.get('fusion-mega-menu')) return;

  const OPEN_DELAY = 70;
  const CLOSE_DELAY = 180;
  const SCROLL_CLOSE_DISTANCE = 120;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class FusionMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.headerWrapper = this.closest('.header-wrapper');
      this.backdrop = this.querySelector('.fusion-mega__backdrop');
      this.panels = Array.from(this.querySelectorAll('.fusion-mega__panel'));
      this.triggers = this.headerWrapper
        ? Array.from(this.headerWrapper.querySelectorAll('[data-fusion-mega-trigger]'))
        : [];

      if (!this.panels.length || !this.triggers.length) return;
      this.initialized = true;

      this.activeKey = null;
      this.activeTrigger = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.openScrollY = 0;

      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onWindowScroll = this.handleWindowScroll.bind(this);
      this.onViewportChange = this.handleViewportChange.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);

      this.bindTriggers();
      this.bindPanels();

      if (this.backdrop) {
        this.backdrop.addEventListener('mouseenter', () => this.scheduleClose());
        this.backdrop.addEventListener('click', () => this.close(false));
      }

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.addEventListener('focusout', this.onFocusOut);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onWindowScroll);
      this.clearTimers();
    }

    bindTriggers() {
      this.triggers.forEach((trigger) => {
        const key = trigger.getAttribute('data-fusion-mega-trigger');

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          if (!this.desktopQuery.matches) return;
          if (this.activeKey === key) {
            this.close(true);
          } else {
            this.open(key);
          }
        });

        trigger.addEventListener('mouseenter', () => {
          if (!this.desktopQuery.matches || !this.hoverQuery.matches) return;
          this.scheduleOpen(key);
        });

        trigger.addEventListener('mouseleave', () => {
          if (!this.desktopQuery.matches || !this.hoverQuery.matches) return;
          this.cancelOpen();
          this.scheduleClose();
        });

        trigger.addEventListener('focusout', this.onFocusOut);

        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open(key);
            this.focusFirstItem(key);
          } else if (event.key === 'Escape' && this.activeKey === key) {
            this.close(true);
          }
        });
      });
    }

    bindPanels() {
      this.panels.forEach((panel) => {
        panel.addEventListener('mouseenter', () => {
          if (!this.desktopQuery.matches || !this.hoverQuery.matches) return;
          this.cancelClose();
        });

        panel.addEventListener('mouseleave', () => {
          if (!this.desktopQuery.matches || !this.hoverQuery.matches) return;
          this.scheduleClose();
        });
      });
    }

    scheduleOpen(key) {
      this.cancelClose();
      this.cancelOpen();
      // Con un panel ya abierto el cambio es inmediato, sin parpadeo intermedio.
      if (this.activeKey) {
        this.open(key);
        return;
      }
      this.openTimer = setTimeout(() => this.open(key), OPEN_DELAY);
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.close(false), CLOSE_DELAY);
    }

    cancelOpen() {
      if (!this.openTimer) return;
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }

    cancelClose() {
      if (!this.closeTimer) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    clearTimers() {
      this.cancelOpen();
      this.cancelClose();
    }

    panelFor(key) {
      return this.panels.find((panel) => panel.dataset.fusionMegaPanel === key);
    }

    open(key) {
      if (!this.desktopQuery.matches) return;

      const panel = this.panelFor(key);
      if (!panel || this.activeKey === key) return;

      this.clearTimers();
      this.closeThemeDisclosures();
      this.setBackdropHeight();

      this.panels.forEach((item) => {
        item.classList.toggle('is-active', item === panel);
        item.setAttribute('aria-hidden', item === panel ? 'false' : 'true');
      });

      this.triggers.forEach((trigger) => {
        const isActive = trigger.getAttribute('data-fusion-mega-trigger') === key;
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        if (isActive) this.activeTrigger = trigger;
      });

      this.classList.add('is-open');
      this.activeKey = key;

      // Evita que el header sticky se esconda mientras el panel esta abierto.
      if (this.headerWrapper) this.headerWrapper.preventHide = true;

      this.openScrollY = window.pageYOffset || document.documentElement.scrollTop;
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    }

    close(restoreFocus) {
      this.clearTimers();
      if (!this.activeKey) return;

      this.panels.forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      });

      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

      this.classList.remove('is-open');
      this.activeKey = null;

      if (this.headerWrapper) this.headerWrapper.preventHide = false;

      window.removeEventListener('scroll', this.onWindowScroll);

      if (restoreFocus === true && this.activeTrigger) this.activeTrigger.focus();
      this.activeTrigger = null;
    }

    focusFirstItem(key) {
      const panel = this.panelFor(key);
      if (!panel) return;
      const first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    // El tema tiene sus propios dropdowns: evitamos dos menus abiertos a la vez.
    closeThemeDisclosures() {
      if (!this.headerWrapper) return;
      this.headerWrapper
        .querySelectorAll('header-menu details[open], products-mega-menu details[open]')
        .forEach((details) => details.removeAttribute('open'));
    }

    setBackdropHeight() {
      if (!this.backdrop) return;
      const top = this.getBoundingClientRect().top;
      const height = Math.max(window.innerHeight - top, 0);
      this.style.setProperty('--fusion-mega-backdrop-height', `${height}px`);
    }

    isInsideMenu(node) {
      if (!node) return false;
      if (this.contains(node)) return true;
      return this.triggers.some((trigger) => trigger.contains(node));
    }

    handleDocumentClick(event) {
      if (!this.activeKey || this.isInsideMenu(event.target)) return;
      this.close(false);
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.activeKey) return;
      this.close(true);
    }

    handleWindowScroll() {
      if (!this.activeKey) return;
      const current = window.pageYOffset || document.documentElement.scrollTop;
      if (Math.abs(current - this.openScrollY) > SCROLL_CLOSE_DISTANCE) this.close(false);
    }

    handleViewportChange() {
      if (!this.desktopQuery.matches) {
        this.close(false);
        return;
      }
      if (this.activeKey) this.setBackdropHeight();
    }

    handleFocusOut() {
      // Se difiere para leer document.activeElement ya actualizado.
      setTimeout(() => {
        if (!this.activeKey) return;
        if (this.isInsideMenu(document.activeElement)) return;
        this.close(false);
      }, 0);
    }
  }

  customElements.define('fusion-mega-menu', FusionMegaMenu);
})();
